import { useCallback, useEffect, useMemo, useState } from "react";
import { TbArrowDown, TbArrowUp, TbArrowsExchange, TbAlertTriangle } from "react-icons/tb";
import { CircularProgress } from "@mui/material";
import {
  StablesInlineValueSkeleton,
  StablesOperationsPanelSkeleton,
} from "../../../Components/Molecules/DashboardSkeleton";
import intl from "react-intl-universal";
import { CurrencyIcon } from "../../../Components/Atoms/CurrencyIcon";
import { useDebouncedValue } from "../../../Hooks/useDebouncedValue";
import { useToast } from "../../../Hooks/toast";
import { useProgressAlert } from "../../../Hooks/useProgressAlert";
import { useStablesBalances } from "../../../Hooks/useStablesBalances";
import { useStablesFees } from "../../../Hooks/useStablesFees";
import { useStablesLimitsContextOptional } from "../../../Contexts/StablesLimitsContext";
import { useStablesWithdrawChains } from "../../../Hooks/useStablesWithdrawChains";
import { formatCurrency } from "../../../Utils/formatters";
import {
  buildConfirmRequestFromQuote,
  confirmStablesOperation,
  createBuyQuote,
  createSellQuote,
  createSwapQuote,
  createWithdrawQuote,
  enrichReceiptFromQuote,
  getStablesApiErrorMessage,
  isOperationReceiptComplete,
} from "../../../Services/stablesApi";
import { buildWithdrawQuote, StablesQuoteModal } from "./StablesQuoteModal";
import {
  computeSwapTransaction,
  computeTradeTransaction,
  computeWithdrawTransaction,
  DEFAULT_STABLE_ASSET_ID,
  formatAmountInput,
  formatPercentAmountInput,
  formatBrl,
  formatStable,
  getAmountInputPlaceholder,
  sanitizeAmountInput,
  parseAmount,
  resolveInsufficientBalance,
  resolveBuyAvailableBrlDisplay,
  resolveBuyPercentBaseBrl,
  resolveMaxBuyStableFromLimitDisplay,
  resolveMaxPurchasableStableDisplay,
  isBuyLimitConfigured,
} from "./constants";

const SELL_PERCENT_OPTIONS = [25, 50, 75, 100];
import { StablesPriceMiniChart } from "./components/StablesPriceMiniChart";
import { StablesOperationQuoteSummary } from "./components/StablesOperationQuoteSummary";
import { useStablesQuoteTimer } from "./hooks/useStablesQuoteTimer";
import { QUOTE_DURATION_SEC } from "./stablesQuoteDisplay";
import { StablesOperationReceiptModal } from "./StablesOperationReceiptModal";
import { StablesTravelRuleFields } from "./StablesTravelRuleFields";
import {
  EMPTY_TRAVEL_RULE_FORM,
  buildTravelRulePayload,
  validateTravelRuleForm,
} from "./travelRuleConstants";

const AssetSelector = ({ value, onChange, exclude, assets }) => {
  const options = assets.filter((a) => a.id !== exclude);

  if (!options.length) {
    return (
      <p className="rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500 dark:border-brand-dark-border dark:text-gray-400">
        {intl.get("stables.swapNoOtherWalletAsset")}
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((asset) => (
        <button
          key={asset.id}
          type="button"
          onClick={() => onChange(asset.id)}
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
            value === asset.id
              ? "border-[#e7a73f] bg-brand-cream text-brand-dark dark:border-brand-gold-mid dark:bg-brand-dark-surface dark:text-gray-100"
              : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-400 dark:hover:border-brand-gold-mid/40"
          }`}
        >
          <CurrencyIcon asset={asset.id} iconUrl={asset.iconUrl} />
          <span>{asset.label}</span>
        </button>
      ))}
    </div>
  );
};

const InputCurrencyToggle = ({ inputCurrency, assetLabel, onChange }) => (
  <div className="mb-3 flex items-center gap-2">
    <div className="stables-segment-control">
      <button
        type="button"
        onClick={() => onChange("brl")}
        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
          inputCurrency === "brl"
            ? "bg-brand-cream text-brand-dark shadow-sm dark:bg-brand-dark-surface dark:text-gray-100"
            : "text-gray-500 hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"
        }`}
      >
        Real (BRL)
      </button>
      <button
        type="button"
        onClick={() => onChange("stable")}
        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
          inputCurrency === "stable"
            ? "bg-brand-cream text-brand-dark shadow-sm dark:bg-brand-dark-surface dark:text-gray-100"
            : "text-gray-500 hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"
        }`}
      >
        {assetLabel}
      </button>
    </div>
    <button
      type="button"
      className="btn-outline-icon"
      aria-label="Alternar moeda de entrada"
      onClick={() => onChange(inputCurrency === "brl" ? "stable" : "brl")}
    >
      <TbArrowsExchange className="h-4 w-4" />
    </button>
  </div>
);

const AvailableBalance = ({ label, value, loading }) => (
  <div className="stables-available-balance mt-3">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </p>
    {loading ? (
      <StablesInlineValueSkeleton width={100} height={20} />
    ) : (
      <p className="mt-1 text-lg font-bold text-brand-dark">{value}</p>
    )}
  </div>
);

const AmountField = ({ label, value, onChange, onBlur, suffix, currencyMode = "brl" }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium text-gray-500">{label}</span>
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={getAmountInputPlaceholder(currencyMode)}
        className="stables-field-input pr-14"
      />
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
          {suffix}
        </span>
      )}
    </div>
  </label>
);

const SellPercentChips = ({ activePercent, onSelect }) => (
  <div className="flex flex-wrap gap-2">
    {SELL_PERCENT_OPTIONS.map((percent) => (
      <button
        key={percent}
        type="button"
        onClick={() => onSelect(percent)}
        className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
          activePercent === percent
            ? "border-[#e7a73f] bg-brand-cream text-brand-dark dark:border-brand-gold-mid dark:bg-brand-dark-surface dark:text-gray-100"
            : "border-gray-200 bg-white text-gray-500 hover:border-[#e7a73f]/40 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-400 dark:hover:border-brand-gold-mid/40"
        }`}
      >
        {percent}%
      </button>
    ))}
  </div>
);

const ChainSelector = ({ value, onChange, chains }) => (
  <div className="flex flex-wrap gap-2">
    {chains.map((chain) => (
      <button
        key={chain.id}
        type="button"
        onClick={() => onChange(chain.id)}
        className={`rounded-lg border px-3 py-2 text-xs font-semibold transition ${
          value === chain.id
            ? "border-[#e7a73f] bg-brand-cream text-brand-dark dark:border-brand-gold-mid dark:bg-brand-dark-surface dark:text-gray-100"
            : "border-gray-200 bg-white text-gray-500 hover:border-gray-300 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-400 dark:hover:border-brand-gold-mid/40"
        }`}
      >
        {chain.label}
      </button>
    ))}
  </div>
);

const InsufficientBalanceAlert = ({ availableDisplay }) => (
  <div
    className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-950/30"
    role="alert"
  >
    <TbAlertTriangle
      className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
      aria-hidden="true"
    />
    <p className="text-sm text-amber-900 dark:text-amber-100">
      {intl.get("stables.insufficientBalance", { available: availableDisplay })}
    </p>
  </div>
);

const OperationSummaryPlaceholder = () => (
  <div className="flex min-h-[220px] flex-1 flex-col items-center justify-center px-2 py-10 text-center">
    <div className="stables-summary-placeholder-icon">
      <div className="flex items-center gap-1 text-brand-dark/75">
        <TbArrowDown className="h-[1.125rem] w-[1.125rem] stroke-[1.75]" />
        <TbArrowUp className="h-[1.125rem] w-[1.125rem] stroke-[1.75]" />
      </div>
    </div>
    <p className="mt-6 max-w-[13rem] text-[0.9375rem] font-semibold leading-snug tracking-tight text-brand-dark">
      Agora, solicite uma cotação
    </p>
  </div>
);

const resolveDefaultAssetId = (assets, preferredId = DEFAULT_STABLE_ASSET_ID) => {
  if (assets.some((item) => item.id === preferredId)) return preferredId;
  return assets[0]?.id ?? preferredId;
};

const resolveDefaultSwapToId = (assets, fromId) => {
  const preferred = fromId === "usdt" ? "usdc" : "usdt";
  const candidate = assets.find((item) => item.id === preferred && item.id !== fromId);
  if (candidate) return candidate.id;
  return assets.find((item) => item.id !== fromId)?.id ?? preferred;
};

export const StablesOperationsPanel = ({
  operation,
  brlBalance = 0,
  brlBalanceFormatted,
  loadingBalance = false,
  accountData = {},
  marketQuotes = [],
  marketQuotesLoading = false,
  onOperationConfirmed,
  /** When set, skip client balances hook and use these assets instead (BaaS). */
  assetsOverride = null,
  balancesLoadingOverride = false,
  balancesErrorOverride = null,
  onReloadBalancesOverride = null,
  /** Prefer / restrict asset & chain selection (wallet-bound BaaS ops). */
  preferredAssetId = null,
  preferredChainId = null,
  restrictChainsToPreferred = false,
  /** Custom quote / confirm adapters (BaaS proxy or alternate APIs). */
  createQuoteFn = null,
  confirmFn = null,
  /** Optional label overrides: { buy, sell, swap, withdraw }. */
  operationLabels = null,
  withdrawSubmitLabel = null,
  /**
   * Optional compra limit disponível (centavos) — e.g. BaaS console entityLimits.
   * When set, takes precedence over StablesLimitsContext for buy max / % chips.
   */
  buyLimitAvailableCentsOverride = null,
  /** When override is used: whether a compra policy is configured (vs unlimited). */
  buyLimitConfiguredOverride = null,
}) => {
  const toast = useToast();
  const progress = useProgressAlert();
  const balancesState = useStablesBalances({
    enabled: !assetsOverride,
    ownedOnly: operation === "swap",
  });
  const assets = assetsOverride ?? balancesState.assets;
  const balancesLoading = assetsOverride
    ? balancesLoadingOverride
    : balancesState.loading;
  const balancesError = assetsOverride
    ? balancesErrorOverride
    : balancesState.error;
  const reloadBalances = assetsOverride
    ? (onReloadBalancesOverride ?? (() => {}))
    : balancesState.reload;
  const {
    fees,
    loading: feesLoading,
    error: feesError,
    reload: reloadFees,
  } = useStablesFees();
  const {
    chains: availableChains,
    loading: chainsLoading,
    error: chainsError,
    reload: reloadChains,
  } = useStablesWithdrawChains();
  const limitsContext = useStablesLimitsContextOptional();
  // 0 = no compra limit configured (unlimited). Never use MAX_SAFE_INTEGER —
  // that was formatted as ~R$ 90 trillion in "Saldo BRL disponível".
  const getOperationLimitAvailableCents =
    limitsContext?.getOperationLimitAvailableCents
    ?? (() => 0);
  const getLimitForOperation =
    limitsContext?.getLimitForOperation
    ?? (() => ({ usedCents: 0, totalCents: 0, availableCents: 0 }));
  const limitsLoading =
    buyLimitAvailableCentsOverride != null
      ? false
      : (limitsContext?.loading ?? false);
  const limitsError =
    buyLimitAvailableCentsOverride != null
      ? null
      : (limitsContext?.error ?? null);
  const chains = useMemo(() => {
    if (!restrictChainsToPreferred || !preferredChainId) return availableChains;
    const filtered = availableChains.filter((item) => item.id === preferredChainId);
    return filtered.length ? filtered : availableChains;
  }, [availableChains, preferredChainId, restrictChainsToPreferred]);
  const [asset, setAsset] = useState(preferredAssetId || DEFAULT_STABLE_ASSET_ID);
  const [inputCurrency, setInputCurrency] = useState("brl");
  const [swapFrom, setSwapFrom] = useState(DEFAULT_STABLE_ASSET_ID);
  const [swapTo, setSwapTo] = useState("usdt");
  const [amount, setAmount] = useState("");
  const [sellPercent, setSellPercent] = useState(null);
  const [withdrawChain, setWithdrawChain] = useState(preferredChainId || "polygon");
  const [destination, setDestination] = useState("");
  const [travelRuleForm, setTravelRuleForm] = useState(EMPTY_TRAVEL_RULE_FORM);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [activeQuote, setActiveQuote] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteRefreshing, setQuoteRefreshing] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [operationReceipt, setOperationReceipt] = useState(null);
  const [quoteSecurityStep, setQuoteSecurityStep] = useState(false);

  const getAsset = (id) => assets.find((item) => item.id === id);
  const getChain = (id) => chains.find((item) => item.id === id);

  const selectedAsset = getAsset(asset);
  const fromAsset = getAsset(swapFrom);
  const toAsset = getAsset(swapTo);
  /** Prefer live market quote unit price for buy max / % conversion when available. */
  const pricedSelectedAsset = useMemo(() => {
    if (!selectedAsset) return null;
    const assetKey = String(selectedAsset.id ?? "").trim().toLowerCase();
    const quote = (marketQuotes ?? []).find(
      (item) => String(item.asset ?? item.id ?? "").trim().toLowerCase() === assetKey
    );
    const quotePrice = Number(quote?.price ?? quote?.rate);
    if (!(quotePrice > 0)) return selectedAsset;
    return {
      ...selectedAsset,
      rate: Number(quotePrice.toFixed(4)),
      priceString: String(quote?.price ?? quote?.rate ?? selectedAsset.priceString ?? quotePrice),
    };
  }, [selectedAsset, marketQuotes]);
  const canSwapPair = useMemo(() => {
    if (operation !== "swap") return true;
    return assets.some((item) => item.id !== swapFrom);
  }, [operation, assets, swapFrom]);
  const selectedChain = getChain(withdrawChain);
  const amountCurrencyMode =
    operation === "swap" || operation === "withdraw"
      ? "stable"
      : inputCurrency === "brl"
        ? "brl"
        : "stable";
  const debouncedAmount = useDebouncedValue(amount, 300);
  const numericAmount = parseAmount(debouncedAmount, amountCurrencyMode);
  const activeTravelRule = useMemo(
    () => buildTravelRulePayload(travelRuleForm, accountData),
    [travelRuleForm, accountData]
  );

  const brlDisplay =
    brlBalanceFormatted || formatCurrency(brlBalance || 0);

  const buyLimitAvailableCents = useMemo(() => {
    if (operation !== "buy") return 0;
    if (buyLimitAvailableCentsOverride != null) {
      return Math.max(0, Math.round(Number(buyLimitAvailableCentsOverride) || 0));
    }
    return getOperationLimitAvailableCents("buy");
  }, [operation, buyLimitAvailableCentsOverride, getOperationLimitAvailableCents]);

  const buyLimitConfigured = useMemo(() => {
    if (operation !== "buy") return false;
    if (buyLimitConfiguredOverride != null) return Boolean(buyLimitConfiguredOverride);
    return isBuyLimitConfigured(getLimitForOperation("buy"));
  }, [operation, buyLimitConfiguredOverride, getLimitForOperation]);

  useEffect(() => {
    if (!assets.length) return;

    // Buy/sell/transfer: keep bound to preferred wallet asset when provided.
    setAsset((current) => {
      if (preferredAssetId && assets.some((item) => item.id === preferredAssetId)) {
        return preferredAssetId;
      }
      return assets.some((item) => item.id === current)
        ? current
        : resolveDefaultAssetId(assets);
    });
    // Swap: never force "De" back to preferred — only fix invalid selections.
    setSwapFrom((current) => {
      if (assets.some((item) => item.id === current)) return current;
      if (preferredAssetId && assets.some((item) => item.id === preferredAssetId)) {
        return preferredAssetId;
      }
      return resolveDefaultAssetId(assets);
    });
  }, [assets, preferredAssetId]);

  useEffect(() => {
    if (!chains.length) return;

    setWithdrawChain((current) => {
      if (preferredChainId && chains.some((item) => item.id === preferredChainId)) {
        return preferredChainId;
      }
      return chains.some((item) => item.id === current) ? current : chains[0].id;
    });
  }, [chains, preferredChainId]);

  useEffect(() => {
    if (!assets.length) return;

    setSwapTo((current) => {
      if (current !== swapFrom && assets.some((item) => item.id === current)) return current;
      return resolveDefaultSwapToId(assets, swapFrom);
    });
  }, [assets, swapFrom]);

  useEffect(() => {
    if (operation === "buy") setInputCurrency("brl");
    else if (operation === "sell" || operation === "withdraw") setInputCurrency("stable");
    setAmount("");
    setSellPercent(null);
    setDestination("");
    setTravelRuleForm(EMPTY_TRAVEL_RULE_FORM);
  }, [operation]);

  useEffect(() => {
    setAmount("");
    setSellPercent(null);
  }, [inputCurrency, asset, withdrawChain, swapFrom]);

  const handleAmountChange = (value) => {
    setAmount(sanitizeAmountInput(value, amountCurrencyMode));
    setSellPercent(null);
  };

  const handleAmountBlur = useCallback(() => {
    if (!amount) return;
    const parsed = parseAmount(amount, amountCurrencyMode);
    if (parsed > 0) {
      setAmount(formatAmountInput(parsed, amountCurrencyMode));
    }
  }, [amount, amountCurrencyMode]);

  const handleSellPercentSelect = (percent) => {
    const sourceAsset = operation === "swap" ? fromAsset : selectedAsset;
    if (!sourceAsset) return;

    const availableBalance =
      operation === "swap" || inputCurrency === "stable"
        ? sourceAsset.balance
        : sourceAsset.balance * sourceAsset.rate;

    const value = availableBalance * (percent / 100);
    // Floor to input decimals so % fills never round above available balance.
    setAmount(formatPercentAmountInput(value, amountCurrencyMode));
    setSellPercent(percent);
  };

  const handleBuyPercentSelect = (percent) => {
    const baseBrl = resolveBuyPercentBaseBrl(brlBalance, buyLimitAvailableCents);
    if (!(baseBrl > 0)) return;
    const value = baseBrl * (percent / 100);
    setAmount(formatPercentAmountInput(value, "brl"));
    setSellPercent(percent);
  };

  const tradeTransaction = useMemo(() => {
    if (["swap", "withdraw"].includes(operation) || !selectedAsset || !numericAmount || !fees) return null;
    return computeTradeTransaction({
      operation,
      inputCurrency,
      numericAmount,
      asset: selectedAsset,
      priceString: selectedAsset.priceString,
      feeConfig: fees,
    });
  }, [operation, inputCurrency, numericAmount, selectedAsset, fees]);

  const swapTransaction = useMemo(() => {
    if (operation !== "swap" || !fromAsset || !toAsset || !numericAmount || !fees) return null;
    return computeSwapTransaction({ fromAsset, toAsset, numericAmount, feeConfig: fees });
  }, [operation, fromAsset, toAsset, numericAmount, fees]);

  const withdrawTransaction = useMemo(() => {
    if (operation !== "withdraw" || !selectedAsset || !selectedChain || !numericAmount || !fees) return null;
    return computeWithdrawTransaction({
      asset: selectedAsset,
      chain: selectedChain,
      amount: numericAmount,
      destination,
      travelRule: activeTravelRule,
      feeConfig: fees,
    });
  }, [operation, selectedAsset, selectedChain, numericAmount, destination, activeTravelRule, fees]);

  const needsChains = operation === "withdraw";
  const dataLoading = balancesLoading || feesLoading || (needsChains && chainsLoading);
  const dataError = balancesError || feesError || (needsChains && chainsError);

  const handleRetryData = () => {
    if (balancesError) reloadBalances();
    if (feesError) reloadFees();
    if (chainsError) reloadChains();
  };

  const dataErrorMessage = balancesError
    ? intl.get("stables.balancesLoadError")
    : feesError
      ? intl.get("stables.feesLoadError")
      : intl.get("stables.chainsLoadError");

  const quoteSummary = useMemo(() => {
    if (activeQuote && ["buy", "sell", "swap"].includes(activeQuote.operation)) {
      return activeQuote;
    }

    if (!numericAmount || numericAmount <= 0) return null;

    if ((operation === "buy" || operation === "sell") && tradeTransaction && selectedAsset) {
      return {
        operation,
        inputCurrency,
        assetLabel: selectedAsset.label,
        price: selectedAsset.priceString ?? String(selectedAsset.rate ?? ""),
        ...tradeTransaction,
      };
    }

    if (operation === "swap" && swapTransaction && fromAsset && toAsset) {
      return {
        operation: "swap",
        fromAssetLabel: fromAsset.label,
        toAssetLabel: toAsset.label,
        price:
          swapTransaction.fromAmount > 0
            ? swapTransaction.netToAmount / swapTransaction.fromAmount
            : 0,
        ...swapTransaction,
      };
    }

    if (operation === "withdraw" && withdrawTransaction && selectedAsset && selectedChain) {
      return {
        operation: "withdraw",
        assetLabel: selectedAsset.label,
        chain: selectedChain.label,
        ...withdrawTransaction,
      };
    }

    return null;
  }, [
    activeQuote,
    operation,
    numericAmount,
    tradeTransaction,
    selectedAsset,
    selectedChain,
    swapTransaction,
    fromAsset,
    toAsset,
    withdrawTransaction,
    inputCurrency,
  ]);

  const availableBalance = useMemo(() => {
    if (operation === "withdraw" && selectedAsset) {
      return {
        label: `Saldo ${selectedAsset.label} disponível`,
        value: formatStable(selectedAsset.balance, selectedAsset.id),
      };
    }

    if (operation === "swap" || !selectedAsset) {
      return { label: "Saldo disponível", value: "—" };
    }

    if (operation === "buy") {
      const maxAsset = pricedSelectedAsset || selectedAsset;
      if (limitsLoading) {
        return {
          label:
            inputCurrency === "brl"
              ? "Limite disponível"
              : `Máximo em ${selectedAsset.label}`,
          value: "—",
          loading: true,
        };
      }

      if (limitsError) {
        return {
          label:
            inputCurrency === "brl"
              ? "Limite disponível"
              : `Máximo em ${selectedAsset.label}`,
          value: "—",
        };
      }

      if (inputCurrency === "brl") {
        return {
          label: "Saldo BRL da conta (fiat)",
          value: resolveBuyAvailableBrlDisplay(
            brlBalance,
            buyLimitAvailableCents,
            brlDisplay
          ),
        };
      }

      // Stable input: max qty from Baas compra limit disponível ÷ market rate.
      // Do not fall back to BRL balance when a limit policy exists (incl. available=0).
      return {
        label: `Máximo em ${selectedAsset.label}`,
        value: buyLimitConfigured
          ? resolveMaxBuyStableFromLimitDisplay(maxAsset, buyLimitAvailableCents)
          : resolveMaxPurchasableStableDisplay(maxAsset, brlBalance, 0),
      };
    }

    if (inputCurrency === "stable") {
      return {
        label: `Saldo ${selectedAsset.label} disponível`,
        value: formatStable(selectedAsset.balance, selectedAsset.id),
      };
    }

    return {
      label: "Máximo a receber em BRL",
      value: numericAmount > 0
        ? formatBrl(tradeTransaction?.netBrlAmount ?? selectedAsset.balance * selectedAsset.rate)
        : formatBrl(0),
    };
  }, [
    operation,
    inputCurrency,
    selectedAsset,
    pricedSelectedAsset,
    brlBalance,
    brlDisplay,
    buyLimitAvailableCents,
    buyLimitConfigured,
    limitsLoading,
    limitsError,
    numericAmount,
    tradeTransaction,
  ]);

  const insufficientBalance = useMemo(
    () =>
      resolveInsufficientBalance({
        operation,
        inputCurrency,
        numericAmount,
        brlBalance,
        buyLimitAvailableCents,
        selectedAsset,
        fromAsset,
        tradeTransaction,
        swapTransaction,
        withdrawTransaction,
        brlDisplay,
      }),
    [
      operation,
      inputCurrency,
      numericAmount,
      brlBalance,
      buyLimitAvailableCents,
      selectedAsset,
      fromAsset,
      tradeTransaction,
      swapTransaction,
      withdrawTransaction,
      brlDisplay,
    ]
  );

  const handleSwapAssets = () => {
    if (!canSwapPair) return;
    // Flip De/Para; Para continues to exclude the new De via exclude={swapFrom}.
    setSwapFrom(swapTo);
    setSwapTo(swapFrom);
  };

  const handleSwapFromChange = (assetId) => {
    setSwapFrom(assetId);
    // De lists all owned stables; if the pick matches Para, move Para to another asset.
    if (assetId === swapTo) {
      setSwapTo(resolveDefaultSwapToId(assets, assetId));
    }
  };

  const closeQuote = useCallback(() => {
    if (isConfirming) return;
    setQuoteOpen(false);
    setActiveQuote(null);
    setQuoteRefreshing(false);
    setPasswordError(null);
    setQuoteSecurityStep(false);
  }, [isConfirming]);

  const resetOperationFormAfterSuccess = useCallback(() => {
    setAmount("");
    setSellPercent(null);
  }, []);

  const fetchActiveQuote = useCallback(async () => {
    if (createQuoteFn) {
      return createQuoteFn({
        operation,
        selectedAsset,
        selectedChain,
        tradeTransaction,
        withdrawTransaction,
        swapTransaction,
        fromAsset,
        toAsset,
        inputCurrency,
        numericAmount,
        destination,
      });
    }

    if (operation === "buy" || operation === "sell") {
      if (!selectedAsset || !tradeTransaction) return null;

      const createQuote = operation === "buy" ? createBuyQuote : createSellQuote;
      const quote = await createQuote({
        assetId: selectedAsset.id,
        inputCurrency,
        amount: numericAmount,
      });
      return {
        ...quote,
        inputCurrency,
        assetLabel: quote.assetLabel ?? selectedAsset.label,
        price: quote.price ?? quote.rate ?? selectedAsset.rate,
      };
    }

    if (operation === "swap") {
      if (!fromAsset || !toAsset || !swapTransaction) return null;

      const quote = await createSwapQuote({
        fromAssetId: fromAsset.id,
        toAssetId: toAsset.id,
        amount: numericAmount,
      });
      return {
        ...quote,
        fromAssetLabel: quote.fromAssetLabel ?? fromAsset.label,
        toAssetLabel: quote.toAssetLabel ?? toAsset.label,
      };
    }

    if (operation === "withdraw") {
      if (!selectedAsset || !selectedChain || !withdrawTransaction) return null;

      const quote = await createWithdrawQuote({
        assetId: selectedAsset.id,
        chainId: selectedChain.id,
        amount: numericAmount,
        destination: destination.trim(),
      });
      return buildWithdrawQuote({
        asset: selectedAsset,
        transaction: {
          ...quote,
          ...withdrawTransaction,
          chain: quote.chain ?? selectedChain.label,
        },
      });
    }

    return null;
  }, [
    createQuoteFn,
    operation,
    selectedAsset,
    tradeTransaction,
    inputCurrency,
    numericAmount,
    fromAsset,
    toAsset,
    swapTransaction,
    selectedChain,
    withdrawTransaction,
    destination,
  ]);

  const handleQuoteExpire = useCallback(async () => {
    if (quoteRefreshing || !quoteOpen) return;

    setQuoteRefreshing(true);
    try {
      const quote = await fetchActiveQuote();
      if (quote) {
        setActiveQuote({ ...quote, refreshedAt: Date.now() });
      } else {
        toast.addToast({
          type: "error",
          title: "Falha ao atualizar cotação",
          description: "Não foi possível renovar a cotação. Feche e solicite novamente.",
        });
      }
    } catch (err) {
      toast.addToast({
        type: "error",
        title: "Falha ao atualizar cotação",
        description: getStablesApiErrorMessage(err, "Não foi possível renovar a cotação. Tente solicitar novamente."),
      });
    } finally {
      setQuoteRefreshing(false);
    }
  }, [quoteRefreshing, quoteOpen, fetchActiveQuote, toast]);

  const quoteDuration = activeQuote?.expiresInSeconds ?? QUOTE_DURATION_SEC;
  const { secondsLeft, barTransition, barWidth } = useStablesQuoteTimer({
    active:
      quoteOpen
      && Boolean(activeQuote)
      && !isConfirming
      && !quoteSecurityStep
      && ["buy", "sell", "swap"].includes(operation),
    quoteKey: `${activeQuote?.id ?? ""}:${activeQuote?.refreshedAt ?? ""}`,
    quoteDuration,
    refreshing: quoteRefreshing,
    onExpire: handleQuoteExpire,
  });

  const passwordStepUpOptions = useMemo(() => {
    if (!activeQuote) return null;

    const amountByOperation = {
      buy: activeQuote.brlAmount,
      sell: activeQuote.stableAmount,
      swap: activeQuote.fromAmount,
      withdraw: activeQuote.stableAmount,
    };

    return {
      transactionId: activeQuote.id,
      amount: amountByOperation[activeQuote.operation] ?? activeQuote.brlAmount,
    };
  }, [activeQuote]);

  const confirmOperation = useCallback(() => {
    const defaultLabels = { buy: "Compra", sell: "Venda", swap: "Swap", withdraw: "Saque" };
    const labels = { ...defaultLabels, ...(operationLabels || {}) };
    const label = labels[activeQuote?.operation] || "Operação";
    const quotedOperation = activeQuote?.operation;
    const quoteSnapshot = activeQuote;

    if (!quotedOperation || !quoteSnapshot) return Promise.resolve();

    setIsConfirming(true);
    setPasswordError(null);

    const confirmPayload = buildConfirmRequestFromQuote(quoteSnapshot, { inputCurrency });

    return progress
      .runWithProgress(
        async () => {
          if (confirmFn) {
            const result = await confirmFn({
              operation: quotedOperation,
              quote: quoteSnapshot,
              inputCurrency,
              confirmPayload,
            });
            return {
              success: true,
              message: result?.message ?? `${label} registrada`,
              description:
                result?.description
                ?? (quotedOperation === "withdraw"
                  ? "Operação em processamento. O hash on-chain será disponibilizado em breve."
                  : "Operação em processamento."),
              transactionId: result.transactionId,
              receipt: result.receipt,
            };
          }

          const result = await confirmStablesOperation(quotedOperation, confirmPayload);
          return {
            success: true,
            message: `${label} registrada`,
            description:
              quotedOperation === "withdraw"
                ? "Operação em processamento. O hash on-chain será disponibilizado em breve."
                : "Operação em processamento.",
            transactionId: result.transactionId,
            receipt: result.receipt,
          };
        },
        {
          loading:
            quotedOperation === "sell"
              ? "Confirmando venda..."
              : `Confirmando ${label.toLowerCase()}...`,
          loadingDescription: "Aguarde enquanto processamos sua operação.",
          error: `Falha ao confirmar ${label.toLowerCase()}`,
          errorDescription: "Não foi possível concluir a operação. Tente novamente.",
        },
        {
          beforeProgress: () => {
            setQuoteOpen(false);
            setActiveQuote(null);
          },
        }
      )
      .then((result) => {
        resetOperationFormAfterSuccess();
        const receipt = enrichReceiptFromQuote(
          {
            ...result?.receipt,
            transactionId:
              result?.receipt?.transactionId
              ?? result?.transactionId,
          },
          quoteSnapshot,
          quotedOperation
        );
        if (isOperationReceiptComplete(receipt) || receipt?.transactionId || receipt?.operation) {
          setOperationReceipt(receipt);
          setReceiptOpen(true);
        }
        reloadBalances?.();
        onOperationConfirmed?.();
      })
      .catch(() => {})
      .finally(() => {
        setIsConfirming(false);
      });
  }, [
    activeQuote,
    inputCurrency,
    progress,
    resetOperationFormAfterSuccess,
    onOperationConfirmed,
    confirmFn,
    operationLabels,
    reloadBalances,
  ]);

  const handlePasswordSubmit = useCallback(
    async (_passwordCombination) => {
      if (isConfirming) return;
      await confirmOperation();
    },
    [confirmOperation, isConfirming]
  );

  const handleSubmit = async () => {
    if (!numericAmount || numericAmount <= 0) {
      toast.addToast({ type: "error", title: "Valor inválido", description: "Informe um valor maior que zero." });
      return;
    }

    if (operation === "buy" || operation === "sell") {
      if (!selectedAsset || !tradeTransaction) return;

      if (insufficientBalance.insufficient) {
        toast.addToast({
          type: "error",
          title: intl.get("stables.insufficientBalanceTitle"),
          description: intl.get("stables.insufficientBalance", {
            available: insufficientBalance.availableDisplay,
          }),
        });
        return;
      }

      setQuoting(true);
      try {
        const quote = await fetchActiveQuote();
        if (quote) {
          setActiveQuote(quote);
          setQuoteOpen(true);
        }
      } catch (err) {
        toast.addToast({
          type: "error",
          title: "Falha ao cotar",
          description: getStablesApiErrorMessage(err, "Não foi possível obter a cotação. Tente novamente."),
        });
      } finally {
        setQuoting(false);
      }
      return;
    }

    if (operation === "swap") {
      if (!fromAsset || !toAsset || !swapTransaction) return;

      if (!(Number(fromAsset.rate) > 0) || !(Number(toAsset.rate) > 0)) {
        toast.addToast({
          type: "error",
          title: "Cotação indisponível",
          description: "Aguarde as cotações das moedas carregarem e tente novamente.",
        });
        return;
      }

      if (insufficientBalance.insufficient) {
        toast.addToast({
          type: "error",
          title: intl.get("stables.insufficientBalanceTitle"),
          description: intl.get("stables.insufficientBalance", {
            available: insufficientBalance.availableDisplay,
          }),
        });
        return;
      }

      setQuoting(true);
      try {
        const quote = await fetchActiveQuote();
        if (quote) {
          setActiveQuote(quote);
          setQuoteOpen(true);
        }
      } catch (err) {
        toast.addToast({
          type: "error",
          title: "Falha ao cotar",
          description: getStablesApiErrorMessage(err, "Não foi possível obter a cotação. Tente novamente."),
        });
      } finally {
        setQuoting(false);
      }
      return;
    }

    if (operation === "withdraw") {
      if (!selectedAsset || !selectedChain || !withdrawTransaction) return;

      if (!destination.trim()) {
        toast.addToast({
          type: "error",
          title: "Destino obrigatório",
          description: "Informe a carteira ou conta de destino para o saque.",
        });
        return;
      }

      const travelRuleValidation = validateTravelRuleForm(travelRuleForm, accountData);
      if (!travelRuleValidation.valid) {
        toast.addToast({
          type: "error",
          title: "Travel Rule incompleto",
          description: travelRuleValidation.message,
        });
        return;
      }

      if (withdrawTransaction.netStableAmount <= 0) {
        toast.addToast({
          type: "error",
          title: "Valor insuficiente",
          description: "O valor deve ser maior que a taxa da operação e da rede.",
        });
        return;
      }

      if (insufficientBalance.insufficient) {
        toast.addToast({
          type: "error",
          title: intl.get("stables.insufficientBalanceTitle"),
          description: intl.get("stables.insufficientBalance", {
            available: insufficientBalance.availableDisplay,
          }),
        });
        return;
      }

      setQuoting(true);
      try {
        const quote = await fetchActiveQuote();
        if (quote) {
          setActiveQuote(quote);
          setQuoteOpen(true);
        }
      } catch (err) {
        toast.addToast({
          type: "error",
          title: "Falha ao cotar",
          description: getStablesApiErrorMessage(err, "Não foi possível obter a cotação. Tente novamente."),
        });
      } finally {
        setQuoting(false);
      }
    }
  };

  const amountLabel =
    inputCurrency === "brl"
      ? "Valor em BRL"
      : `Quantidade em ${selectedAsset?.label || "Stable"}`;

  const amountSuffix = inputCurrency === "brl" ? "BRL" : selectedAsset?.label;

  return (
    <section className="mb-6 min-w-0 overflow-hidden">
      {dataLoading ? (
        <StablesOperationsPanelSkeleton />
      ) : dataError ? (
        <div className="app-card app-empty-state">
          <p>{dataErrorMessage}</p>
          <button type="button" className="btn-outline mt-2 !rounded-full !px-4" onClick={handleRetryData}>
            {intl.get("stables.retryLoad")}
          </button>
        </div>
      ) : (
      <div className="app-card overflow-hidden">
        <div className="grid gap-5 p-4 md:grid-cols-2 md:p-5">
          <div className="space-y-4">
            {operation === "withdraw" ? (
              <div className="space-y-4">
                <div>
                  <p className="mb-2 text-xs font-medium text-gray-500">Stablecoin</p>
                  <AssetSelector value={asset} onChange={setAsset} assets={assets} />
                </div>
                {insufficientBalance.insufficient && (
                  <InsufficientBalanceAlert availableDisplay={insufficientBalance.availableDisplay} />
                )}
                <div>
                  <p className="mb-2 text-xs font-medium text-gray-500">Rede (chain)</p>
                  <ChainSelector value={withdrawChain} onChange={setWithdrawChain} chains={chains} />
                </div>
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-gray-500">Destino</span>
                  <input
                    type="text"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder="Carteira externa ou conta bancária"
                    className="stables-field-input"
                  />
                </label>
                <StablesTravelRuleFields
                  form={travelRuleForm}
                  onChange={setTravelRuleForm}
                  accountData={accountData}
                />
              </div>
            ) : operation !== "swap" ? (
              <div className="space-y-3">
                <div>
                  <p className="mb-2 text-xs font-medium text-gray-500">Stablecoin</p>
                  <AssetSelector value={asset} onChange={setAsset} assets={assets} />
                </div>
                {insufficientBalance.insufficient && (
                  <InsufficientBalanceAlert availableDisplay={insufficientBalance.availableDisplay} />
                )}
                {operation === "buy" && (
                  <StablesPriceMiniChart
                    assetId={asset}
                    marketQuotes={marketQuotes}
                    marketQuotesLoading={marketQuotesLoading}
                  />
                )}
                {operation === "sell" && (
                  <StablesPriceMiniChart
                    assetId={asset}
                    marketQuotes={marketQuotes}
                    marketQuotesLoading={marketQuotesLoading}
                  />
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="mb-2 text-xs font-medium text-gray-500">De</p>
                  {/* De: all owned wallet stables (no exclude). */}
                  <AssetSelector
                    value={swapFrom}
                    onChange={handleSwapFromChange}
                    assets={assets}
                  />
                </div>
                <StablesPriceMiniChart
                  assetId={swapFrom}
                  marketQuotes={marketQuotes}
                  marketQuotesLoading={marketQuotesLoading}
                />
                <button
                  type="button"
                  onClick={handleSwapAssets}
                  className="btn-outline-icon"
                  aria-label="Inverter moedas"
                  disabled={!canSwapPair}
                >
                  <TbArrowsExchange className="h-4 w-4" />
                </button>
                <div>
                  <p className="mb-2 text-xs font-medium text-gray-500">Para</p>
                  {/* Para: owned stables except the De selection — never De === Para. */}
                  <AssetSelector
                    value={swapTo}
                    onChange={setSwapTo}
                    exclude={swapFrom}
                    assets={assets}
                  />
                </div>
                {!canSwapPair ? (
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {intl.get("stables.swapNeedAnotherWalletAsset")}
                  </p>
                ) : null}
                {insufficientBalance.insufficient && (
                  <InsufficientBalanceAlert availableDisplay={insufficientBalance.availableDisplay} />
                )}
              </div>
            )}

            {operation !== "swap" && operation !== "withdraw" && selectedAsset && (
              <InputCurrencyToggle
                inputCurrency={inputCurrency}
                assetLabel={selectedAsset.label}
                onChange={setInputCurrency}
              />
            )}

            {operation === "swap" ? (
              <AmountField
                label={`Quantidade em ${fromAsset?.label}`}
                value={amount}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                suffix={fromAsset?.label}
                currencyMode="stable"
              />
            ) : operation === "withdraw" ? (
              <AmountField
                label={`Quantidade em ${selectedAsset?.label}`}
                value={amount}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                suffix={selectedAsset?.label}
                currencyMode="stable"
              />
            ) : (
              <AmountField
                label={amountLabel}
                value={amount}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                suffix={amountSuffix}
                currencyMode={amountCurrencyMode}
              />
            )}

            {((operation === "sell" && selectedAsset) || (operation === "swap" && fromAsset)) && (
              <SellPercentChips
                activePercent={sellPercent}
                onSelect={handleSellPercentSelect}
              />
            )}

            {operation === "buy" && inputCurrency === "brl" && selectedAsset && (
              <SellPercentChips
                activePercent={sellPercent}
                onSelect={handleBuyPercentSelect}
              />
            )}

            {operation !== "swap" && (
              <AvailableBalance
                label={availableBalance.label}
                value={availableBalance.value}
                loading={Boolean(availableBalance.loading)}
              />
            )}

            <button
              type="button"
              className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2"
              onClick={handleSubmit}
              disabled={
                quoting
                || !numericAmount
                || numericAmount <= 0
                || insufficientBalance.insufficient
                || (operation === "swap" && (
                  !canSwapPair
                  || !toAsset
                  || !(Number(fromAsset?.rate) > 0)
                  || !(Number(toAsset?.rate) > 0)
                  || marketQuotesLoading
                ))
              }
            >
              {quoting ? (
                <>
                  <CircularProgress size={16} color="inherit" />
                  Solicitando cotação...
                </>
              ) : operation === "withdraw" ? (
                withdrawSubmitLabel || intl.get("stables.confirmWithdraw")
              ) : (
                "Solicitar cotação"
              )}
            </button>
          </div>

          <div className="stables-operation-summary">
            <p className="text-xs font-semibold tracking-wide text-gray-500">Resumo da operação</p>

            {quoteSummary ? (
              <div className="mt-3 flex-1">
                <StablesOperationQuoteSummary
                  operation={operation}
                  quote={quoteSummary}
                />
              </div>
            ) : (
              <OperationSummaryPlaceholder />
            )}

            <p className="mt-auto border-t border-gray-100/90 pt-4 text-[11px] leading-relaxed text-gray-400 dark:border-brand-dark-border">
              Valores estimados com base na cotação atual. A taxa final pode variar no momento da confirmação.
            </p>
          </div>
        </div>
      </div>
      )}

      <StablesQuoteModal
        open={quoteOpen}
        quote={activeQuote}
        refreshing={quoteRefreshing}
        onClose={closeQuote}
        onExpire={handleQuoteExpire}
        onStepChange={setQuoteSecurityStep}
        timerProps={
          operation === "withdraw"
            ? null
            : {
                secondsLeft,
                barTransition,
                barWidth,
              }
        }
        submitPassword={handlePasswordSubmit}
        passwordError={passwordError}
        passwordLoading={isConfirming}
        onDismissPasswordError={() => setPasswordError(null)}
        stepUpOptions={passwordStepUpOptions}
      />

      <StablesOperationReceiptModal
        open={receiptOpen}
        receipt={operationReceipt}
        onClose={() => {
          setReceiptOpen(false);
          setOperationReceipt(null);
        }}
      />
    </section>
  );
};
